import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import { EndUserRequestUpgradeModalEntrypoint } from './EndUserRequestUpgradeModal/EndUserRequestUpgradeModalEntrypoint.ts';
import { editionAnalytics } from './analytics/editionAnalytics.ts';

type EndUserRequestUpgradeButtonProps = {
	source: string;
	requestUpgradeButtonIsLoading?: boolean;
	onRequestUpgradeButtonClick?: () => void;
};

export const EndUserRequestUpgradeButton = ({
	source,
	requestUpgradeButtonIsLoading = false,
	onRequestUpgradeButtonClick,
}: EndUserRequestUpgradeButtonProps): React.JSX.Element | null => {
	const { edition } = useSessionData();
	const hasSentViewedEvent = useRef(false);
	const [showModal, setShowModal] = useState(false);
	const [isLoadingModal, setIsLoadingModal] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleCloseModal = () => {
		setShowModal(false);
	};

	const handleOpenModal = () => {
		if (onRequestUpgradeButtonClick) {
			// If onRequestUpgradeButtonClick is provided, call it let it handle the modal opening
			onRequestUpgradeButtonClick();
		} else {
			setIsLoadingModal(true);
			setShowModal(true);
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'endUserRequestUpgradeButton',
				source,
				attributes: {
					...editionAnalytics(edition),
				},
			},
		}).fire();
	};

	useEffect(() => {
		if (hasSentViewedEvent.current) {
			return;
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'button',
				actionSubjectId: 'endUserRequestUpgradeButton',
				source,
				attributes: {
					...editionAnalytics(edition),
				},
			},
		}).fire();
		hasSentViewedEvent.current = true;
	}, [createAnalyticsEvent, edition, source]);

	return (
		<ErrorBoundary
			attribution={edition === ConfluenceEdition.STANDARD ? 'growth-luna' : 'growth-transmuters'}
		>
			<Button
				testId="experiment-confluence-enable-end-user-request.request-upgrade-button"
				onClick={handleOpenModal}
				appearance="primary"
				isLoading={isLoadingModal || requestUpgradeButtonIsLoading}
			>
				<FormattedMessage
					id="experiment-confluence-enable-end-user-request.request-upgrade-button.label"
					defaultMessage="Request to try"
					description="Label text for the request upgrade button in the confluence enable end user request experiment"
				/>
			</Button>
			<ModalTransition>
				{showModal && (
					<EndUserRequestUpgradeModalEntrypoint
						source={source}
						handleCloseModal={handleCloseModal}
						setIsLoadingModal={setIsLoadingModal}
					/>
				)}
			</ModalTransition>
		</ErrorBoundary>
	);
};
